<template>
  <div class="min-h-screen bg-gray-50">
    <div class="bg-white shadow-sm border-b border-gray-200">
      <div class="w-full px-4 sm:px-6 py-6 flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Все проекты</h1>
        </div>
      </div>
    </div>

    <div class="w-full px-4 sm:px-6 py-8">
      <div
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6"
      >
        <div class="flex flex-wrap items-center gap-4">
          <button
            @click="filtersOpen = !filtersOpen"
            :class="[
              filtersOpen
                ? 'border-blue-500 text-blue-600'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50',
              'inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg bg-white transition-colors duration-150'
            ]"
          >
            <i
              :class="[
                filtersOpen ? 'pi-chevron-up' : 'pi-chevron-down',
                'pi mr-2 text-xs'
              ]"
            ></i>
            Фильтры
          </button>

          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-700">Сортировка:</span>
            <select
              v-model="sortField"
              class="px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option
                v-for="column in sortableColumns"
                :key="column.field"
                :value="column.field"
              >
                {{ column.label }}
              </option>
            </select>
            <button
              @click="sortAsc = !sortAsc"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-150"
              :title="sortAsc ? 'По возрастанию' : 'По убыванию'"
            >
              <i :class="[sortAsc ? 'pi-arrow-up' : 'pi-arrow-down', 'pi']"></i>
            </button>
          </div>

          <button
            @click="resetFilters"
            class="ml-auto inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            <i class="pi pi-refresh mr-2 text-xs"></i> Сбросить
          </button>
        </div>

        <div
          v-if="filtersOpen"
          class="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-4"
        >
          <div class="relative flex-1 min-w-[240px] max-w-sm">
            <i
              class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
            ></i>
            <input
              v-model.trim="search"
              type="text"
              placeholder="Поиск по ID или названию"
              class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div
            ref="spaceDropdownRef"
            class="relative flex items-center space-x-2"
          >
            <label class="text-sm text-gray-700">Пространство:</label>
            <button
              type="button"
              @click="toggleSpaceDropdown"
              class="inline-flex items-center justify-between min-w-[180px] max-w-[280px] px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <span class="truncate">{{ selectedSpaceLabel }}</span>
              <i
                :class="[
                  spaceOpen ? 'pi-chevron-up' : 'pi-chevron-down',
                  'pi ml-3 flex-shrink-0 text-xs text-gray-400'
                ]"
              ></i>
            </button>
            <div
              v-if="spaceOpen"
              class="absolute left-0 top-full z-20 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg"
            >
              <div class="p-2 border-b border-gray-100">
                <div class="relative">
                  <i
                    class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                  ></i>
                  <input
                    ref="spaceSearchRef"
                    v-model.trim="spaceSearch"
                    type="text"
                    placeholder="Поиск пространства"
                    class="w-full pl-9 pr-3 py-2 border border-blue-500 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div class="max-h-72 overflow-y-auto py-1">
                <button
                  type="button"
                  @click="selectSpace('')"
                  :class="[
                    spaceFilter === ''
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700',
                    'w-full px-3 py-2 text-left text-sm hover:bg-gray-50'
                  ]"
                >
                  все
                </button>
                <button
                  v-for="space in filteredSpaces"
                  :key="space.id"
                  type="button"
                  @click="selectSpace(space.id)"
                  :class="[
                    spaceFilter === space.id ? 'bg-blue-50' : '',
                    'w-full flex items-start px-3 py-2 text-left hover:bg-gray-50'
                  ]"
                >
                  <span class="min-w-0">
                    <span class="block text-sm text-gray-900 break-words">
                      {{ space.name }}
                    </span>
                    <span class="block text-xs text-gray-400">
                      {{ space.id }}
                    </span>
                  </span>
                </button>
                <div
                  v-if="filteredSpaces.length === 0"
                  class="px-3 py-4 text-center text-sm text-gray-400"
                >
                  Ничего не найдено
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center space-x-3">
            <label class="text-sm text-gray-700">Источник:</label>
            <select
              v-model="sourceFilter"
              class="pl-3 pr-8 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">все</option>
              <option
                v-for="(label, key) in sourceLabels"
                :key="key"
                :value="key"
              >
                {{ label }}
              </option>
            </select>
          </div>

          <label class="inline-flex items-center space-x-2 cursor-pointer">
            <input
              v-model="showArchived"
              type="checkbox"
              class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Показать архивные</span>
          </label>
          <label class="inline-flex items-center space-x-2 cursor-pointer">
            <input
              v-model="showDeleted"
              type="checkbox"
              class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Показать удаленные</span>
          </label>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>

      <div
        v-else-if="noData"
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div class="flex items-center">
          <i class="pi pi-inbox text-gray-400 text-xl"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-gray-900">Данных нет</h3>
          </div>
        </div>
      </div>

      <div
        v-else-if="error"
        class="bg-white rounded-lg shadow-sm border border-red-200 p-6"
      >
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle text-red-400 text-xl"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Ошибка загрузки</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>

      <div
        v-else-if="filteredProjects.length === 0"
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500 text-lg"
      >
        Список пуст
      </div>

      <div v-else class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table
            class="w-full min-w-[1180px] table-fixed divide-y divide-gray-200"
          >
            <colgroup>
              <col class="w-20" />
              <col class="min-w-[200px]" />
              <col class="w-28" />
              <col class="w-40" />
              <col class="w-28" />
              <col class="w-32" />
              <col class="w-32" />
              <col class="w-44" />
              <col class="w-36" />
              <col class="w-28" />
              <col class="w-16" />
            </colgroup>
            <thead class="bg-gray-50">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.field"
                  @click="column.sortable !== false && toggleSort(column.field)"
                  :class="[
                    column.sortable === false
                      ? 'cursor-default'
                      : 'cursor-pointer hover:text-gray-700',
                    'px-3 py-3 align-top text-xs font-medium text-gray-500 uppercase tracking-wider select-none'
                  ]"
                >
                  <div
                    :class="[
                      column.align === 'right'
                        ? 'justify-end'
                        : 'justify-start',
                      'flex items-start gap-1 min-w-0'
                    ]"
                  >
                    <span
                      :class="[
                        column.nowrap ? 'whitespace-nowrap' : 'break-words',
                        'min-w-0'
                      ]"
                      >{{ column.header }}</span
                    >
                    <i
                      v-if="sortField === column.field"
                      :class="[
                        sortAsc ? 'pi-arrow-up' : 'pi-arrow-down',
                        'pi flex-shrink-0 mt-px text-xs text-blue-600'
                      ]"
                    ></i>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="project in filteredProjects"
                :key="project.key"
                @click="openDetail(project)"
                :class="[
                  project.is_archived || project.is_deleted
                    ? 'bg-gray-100 text-gray-400'
                    : 'hover:bg-blue-50',
                  selectedProject && selectedProject.key === project.key
                    ? 'bg-blue-50'
                    : '',
                  'transition-colors duration-150 cursor-pointer'
                ]"
              >
                <td
                  class="px-3 py-4 align-top text-sm font-medium text-gray-400"
                >
                  {{ project.id }}
                </td>
                <td class="px-3 py-4 align-top text-sm font-medium break-words">
                  <a
                    :href="buildReportUrl(project)"
                    target="_blank"
                    rel="noopener"
                    class="text-gray-900 hover:text-blue-600 transition-colors"
                    @click.stop
                  >
                    {{ project.name }}
                  </a>
                </td>
                <td class="px-3 py-4 align-top">
                  <span
                    :class="[
                      sourceClasses[project.source] ||
                        'bg-gray-100 text-gray-600',
                      'inline-flex px-2 py-0.5 rounded text-xs font-medium'
                    ]"
                  >
                    {{ sourceLabels[project.source] || project.source }}
                  </span>
                </td>
                <td class="px-3 py-4 align-top text-sm break-words">
                  <a
                    :href="buildSpaceSettingsUrl(project)"
                    target="_blank"
                    rel="noopener"
                    class="text-gray-900 hover:underline"
                    @click.stop
                  >
                    {{ project.space }} ({{ project.space_id }})
                  </a>
                </td>
                <td class="px-3 py-4 align-top text-sm text-right">
                  {{ project.regions_count }}
                </td>
                <td class="px-3 py-4 align-top text-sm text-right">
                  {{ project.devices_count }}
                </td>
                <td class="px-3 py-4 align-top text-sm">
                  {{ project.schedule }}
                </td>
                <td class="px-3 py-4 align-top">
                  <span
                    :class="[
                      statusClasses[project.status] ||
                        'bg-gray-100 text-gray-600',
                      'inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold'
                    ]"
                  >
                    {{ statusLabels[project.status] || project.status }}
                  </span>
                  <span
                    v-if="isLimitExceeded(project)"
                    class="mt-1 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold whitespace-nowrap"
                  >
                    <i class="pi pi-ban text-xs"></i>
                    Лимит исчерпан
                  </span>
                </td>
                <td class="px-3 py-4 align-top text-sm text-right">
                  {{ project.queries.toLocaleString() }}
                </td>
                <td class="px-3 py-4 align-top text-sm text-gray-500">
                  <div>{{ formatDate(project.last_sync) }}</div>
                  <div class="text-xs text-gray-400">
                    {{ formatTime(project.last_sync) }}
                  </div>
                </td>

                <td class="px-3 py-4 align-top text-right">
                  <ActionsDropdown
                    v-if="!project.is_deleted"
                    :actions="rowProjectActions"
                    :open="rowActionsOpenKey === project.key"
                    :dropdown-id="project.key"
                    @toggle="toggleRowActions(project, $event)"
                    @select="handleRowActionClick"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="selectedProject"
        class="fixed inset-0 z-40 bg-gray-500 bg-opacity-75"
        @click="closeDetail"
      ></div>
    </transition>
    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-x-full"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-to-class="translate-x-full"
    >
      <aside
        v-if="selectedProject"
        class="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-xl flex flex-col"
      >
        <div
          class="flex items-center justify-between gap-4 px-6 py-5 border-b border-gray-200"
        >
          <h2 class="text-lg font-semibold text-gray-900 break-words min-w-0">
            {{ selectedProject.name }}
          </h2>
          <div class="flex items-center gap-2 flex-shrink-0">
            <template v-if="!selectedProject.is_deleted">
              <button
                class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                @click="handleOpenReports"
              >
                <i class="pi pi-chart-bar text-sm"></i>
                Отчёты
              </button>

              <ActionsDropdown
                :actions="projectActions"
                :open="actionsOpen"
                variant="bordered"
                dropdown-id="panel"
                @toggle="actionsOpen = !actionsOpen"
                @select="handleActionClick"
              />
            </template>

            <button
              @click="closeDetail"
              class="p-2 -m-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors duration-150"
              title="Закрыть"
            >
              <i class="pi pi-times text-lg"></i>
            </button>
          </div>
        </div>

        <div class="flex gap-6 px-6 border-b border-gray-200">
          <button
            class="relative py-3 text-sm font-medium transition-colors"
            :class="
              activeTab === 'overview'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            "
            @click="activeTab = 'overview'"
          >
            Обзор
            <span
              v-if="activeTab === 'overview'"
              class="absolute inset-x-0 -bottom-px h-0.5 bg-blue-600"
            ></span>
          </button>
          <button
            class="relative py-3 text-sm font-medium transition-colors flex items-center gap-2"
            :class="
              activeTab === 'history'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            "
            @click="activeTab = 'history'"
          >
            История
            <span
              v-if="historyItems.length"
              class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-semibold"
            >
              {{ historyItems.length }}
            </span>
            <span
              v-if="activeTab === 'history'"
              class="absolute inset-x-0 -bottom-px h-0.5 bg-blue-600"
            ></span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <div v-if="activeTab === 'overview'" class="space-y-6">
            <section
              v-for="(section, index) in detailSections"
              :key="section.title"
              :class="index > 0 ? 'pt-6 border-t border-gray-200' : ''"
            >
              <h3 class="text-sm font-semibold text-gray-900 mb-4">
                {{ section.title }}
              </h3>
              <dl class="space-y-3">
                <div
                  v-for="row in section.rows"
                  :key="row.label"
                  class="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-4"
                >
                  <dt class="text-sm text-gray-500 break-words">
                    {{ row.label }}
                  </dt>
                  <dd class="text-sm text-gray-900 break-words">
                    <span
                      v-if="row.badge"
                      :class="[
                        row.badgeClass,
                        'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold'
                      ]"
                    >
                      <i
                        v-if="row.label === 'Лимиты'"
                        class="pi pi-ban text-xs"
                      ></i>
                      {{ row.value }}
                    </span>
                    <span v-else>{{ row.value }}</span>
                  </dd>
                </div>
              </dl>
              <a
                v-if="section.action && section.action.href"
                :href="section.action.href"
                target="_blank"
                rel="noopener"
                class="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-150"
              >
                <i :class="['pi', section.action.icon, 'mr-2 text-sm']"></i>
                {{ section.action.label }}
              </a>
            </section>
          </div>

          <div v-else>
            <div v-if="historyLoading" class="flex justify-center py-8">
              <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
              ></div>
            </div>

            <div
              v-else-if="historyError"
              class="py-8 text-center text-sm text-red-600"
            >
              {{ historyError }}
            </div>

            <div
              v-else-if="historyItems.length === 0"
              class="py-8 text-center text-sm text-gray-400"
            >
              История пуста
            </div>

            <div v-else class="divide-y divide-gray-100">
              <div
                v-for="(item, index) in historyItems"
                :key="index"
                class="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-4 py-3"
              >
                <div class="text-sm text-gray-400">
                  {{ formatHistoryDate(item.time) }}
                </div>
                <div class="text-sm text-gray-900">
                  {{ item.actor }} - {{ item.description }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </transition>

    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showRenameDialog"
        class="fixed inset-0 flex items-center justify-center p-4 z-[200]"
      >
        <div
          class="absolute inset-0 bg-gray-500 bg-opacity-75"
          @click="showRenameDialog = false"
        ></div>
        <div
          class="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        >
          <div class="flex items-start gap-4 mb-5">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center"
            >
              <i class="pi pi-pencil text-blue-500"></i>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                Переименование проекта
              </h3>
              <p class="text-sm text-gray-500 mt-0.5">
                Текущее: «{{ selectedProject?.name }}», ID
                {{ selectedProject?.id }}
              </p>
            </div>
          </div>

          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Новое название</label
          >
          <input
            v-model="renameNewName"
            type="text"
            class="w-full px-3 py-2 border border-blue-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keydown.enter="submitRename"
          />

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="showRenameDialog = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              @click="submitRename"
              :disabled="renameLoading || !renameNewName.trim()"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i v-if="renameLoading" class="pi pi-spin pi-spinner mr-2"></i>
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showCopyDialog"
        class="fixed inset-0 flex items-center justify-center p-4 z-[200]"
      >
        <div
          class="absolute inset-0 bg-gray-500 bg-opacity-75"
          @click="showCopyDialog = false"
        ></div>
        <div
          class="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        >
          <div class="flex items-start gap-4 mb-5">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center"
            >
              <i class="pi pi-copy text-blue-500"></i>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                Копировать проект
              </h3>
              <p class="text-sm text-gray-500 mt-0.5">
                Создастся копия проекта с настройками семантики и сборов в
                выбранном пространстве.
              </p>
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Источник</label
              >
              <input
                :value="`${selectedProject?.name} (ID ${selectedProject?.id})`"
                type="text"
                readonly
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Новое название</label
              >
              <input
                v-model="copyNewName"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Целевое пространство</label
              >
              <select
                v-model="copyTargetSpaceId"
                class="w-full px-3 py-2 border border-blue-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option
                  v-for="space in copyAvailableSpaces"
                  :key="space.id"
                  :value="space.id"
                >
                  {{ space.name }} (ID {{ space.id }})
                </option>
              </select>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="showCopyDialog = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              @click="submitCopy"
              :disabled="
                copyLoading || !copyNewName.trim() || !copyTargetSpaceId
              "
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i v-if="copyLoading" class="pi pi-spin pi-spinner"></i>
              <i v-else class="pi pi-copy"></i>
              Создать копию
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showArchiveConfirm"
        class="fixed inset-0 flex items-center justify-center p-4 z-[200]"
      >
        <div
          class="absolute inset-0 bg-gray-500 bg-opacity-75"
          @click="showArchiveConfirm = false"
        ></div>
        <div
          class="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        >
          <div class="flex items-start gap-4 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center"
            >
              <i class="pi pi-exclamation-triangle text-orange-500"></i>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                Перенести проект в архив?
              </h3>
              <p class="text-sm text-gray-500 mt-1">
                «{{ selectedProject?.name }}» будет перенесён в архив. Сборы
                остановятся, проект перестанет учитываться в лимитах.
                Восстановить можно в любой момент.
              </p>
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <button
              @click="showArchiveConfirm = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              @click="submitArchive"
              :disabled="archiveLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i v-if="archiveLoading" class="pi pi-spin pi-spinner mr-2"></i>
              В архив
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 flex items-center justify-center p-4 z-[200]"
      >
        <div
          class="absolute inset-0 bg-gray-500 bg-opacity-75"
          @click="showDeleteConfirm = false"
        ></div>
        <div
          class="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        >
          <div class="flex items-start gap-4 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center"
            >
              <i class="pi pi-trash text-red-500"></i>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                Удалить проект безвозвратно?
              </h3>
              <p class="text-sm text-gray-500 mt-1">
                «{{ selectedProject?.name }}» (ID {{ selectedProject?.id }})
                будет удалён полностью со всеми данными. Это действие
                необратимо.
              </p>
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <button
              @click="showDeleteConfirm = false"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
            <button
              @click="submitDelete"
              :disabled="deleteLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i v-if="deleteLoading" class="pi pi-spin pi-spinner mr-2"></i>
              Удалить
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import {
    ref,
    computed,
    nextTick,
    onMounted,
    onBeforeUnmount,
    watch
  } from 'vue'
  import { useToast } from 'primevue/usetoast'
  import { projectsApi, reportingApi } from '../services/admin'
  import { limitsApi } from '../services/api.js'
  import ActionsDropdown from '../components/ActionDropDown.vue'

  const toast = useToast()

  const projects = ref([])
  const loading = ref(false)
  const error = ref('')
  const noData = ref(false)

  const filtersOpen = ref(false)
  const search = ref('')
  const sourceFilter = ref('')
  const showArchived = ref(false)
  const showDeleted = ref(false)
  const spaceFilter = ref('')
  const spaceOpen = ref(false)
  const spaceSearch = ref('')
  const spaceDropdownRef = ref(null)
  const spaceSearchRef = ref(null)
  const sortField = ref('id')
  const sortAsc = ref(true)

  const selectedProject = ref(null)
  const actionsOpen = ref(false)
  const limitStoppedSpaces = ref(new Set())
  const activeTab = ref('overview')

  const historyItems = ref([])
  const historyLoading = ref(false)
  const historyError = ref('')
  const rowActionsOpenKey = ref(null)
  const rowActionsProject = ref(null)

  const showRenameDialog = ref(false)
  const renameNewName = ref('')
  const renameLoading = ref(false)
  const showCopyDialog = ref(false)
  const copyNewName = ref('')
  const copyTargetSpaceId = ref(null)
  const copyLoading = ref(false)
  const showArchiveConfirm = ref(false)
  const archiveLoading = ref(false)
  const showDeleteConfirm = ref(false)
  const deleteLoading = ref(false)

  const SOURCE_TO_APPLICATION = { dfAi: 'ai', direct: 'context' }

  const REPORT_URLS = {
    dfAi: 'https://ai.dataforce.io/projects/{id}/competitors',
    direct: 'https://direct.dataforce.io/projects/{id}/competitors'
  }

  const SETTINGS_URLS = {
    dfAi: 'https://ai.dataforce.io/projects/{id}/settings/1',
    direct: 'https://direct.dataforce.io/projects/{id}/settings/1'
  }

  const SPACE_SETTINGS_URLS = {
    dfAi: 'https://ai.dataforce.io/spaces/{id}/projects',
    direct: 'https://direct.dataforce.io/spaces/{id}/projects'
  }

  const buildUrl = (urlMap, project) => {
    const template = urlMap[project.source]
    if (!template || project.id == null) return ''
    return template.replace('{id}', project.id)
  }

  const buildReportUrl = project => buildUrl(REPORT_URLS, project)
  const buildSettingsUrl = project => buildUrl(SETTINGS_URLS, project)

  const buildSpaceSettingsUrl = project => {
    const template = SPACE_SETTINGS_URLS[project.source]
    if (!template || project.space_id == null) return ''
    return template.replace('{id}', project.space_id)
  }

  const limitKey = (application, spaceId) => `${application}-${spaceId}`
  const getApplication = project => SOURCE_TO_APPLICATION[project.source]

  const isLimitExceeded = project =>
    project.space_id != null &&
    limitStoppedSpaces.value.has(
      limitKey(getApplication(project), project.space_id)
    )

  const notifyError = e =>
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: e?.message || 'Ошибка выполнения запроса',
      life: 3000
    })

  const notifySuccess = detail =>
    toast.add({ severity: 'success', summary: 'Готово', detail, life: 3000 })

  const handleOpenReports = () => {
    const url = buildReportUrl(selectedProject.value)
    if (url) window.open(url, '_blank')
  }

  const openRenameDialog = () => {
    renameNewName.value = selectedProject.value?.name ?? ''
    showRenameDialog.value = true
    actionsOpen.value = false
  }

  const submitRename = async () => {
    const project = selectedProject.value
    if (!project || !renameNewName.value.trim()) return
    renameLoading.value = true
    try {
      await projectsApi.update(project.id, {
        name: renameNewName.value.trim(),
        application: getApplication(project)
      })
      notifySuccess('Проект переименован')
      showRenameDialog.value = false
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    } finally {
      renameLoading.value = false
    }
  }

  const toggleSync = async () => {
    const project = selectedProject.value
    if (!project) return
    const isCollecting = project.status === 'collecting'
    actionsOpen.value = false
    try {
      await projectsApi.subAction(project.id, 'root-semantics-folders/sync', {
        action: isCollecting ? 'deactivate' : 'activate',
        space_id: project.space_id,
        filters: { project_ids: [String(project.id)] },
        application: getApplication(project)
      })
      notifySuccess(isCollecting ? 'Сборы отключены' : 'Сборы включены')
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    }
  }

  const openCopyDialog = () => {
    const project = selectedProject.value
    if (!project) return
    copyNewName.value = `${project.name} (копия)`
    copyTargetSpaceId.value = project.space_id ?? null
    showCopyDialog.value = true
    actionsOpen.value = false
  }

  const submitCopy = async () => {
    const project = selectedProject.value
    if (!project) return
    copyLoading.value = true
    try {
      await projectsApi.action(project.id, {
        action: 'copy',
        application: getApplication(project),
        project_name: copyNewName.value.trim(),
        space_id: copyTargetSpaceId.value,
        need_copy_production_data: false
      })
      notifySuccess('Копия проекта создана')
      showCopyDialog.value = false
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    } finally {
      copyLoading.value = false
    }
  }

  const copyAvailableSpaces = computed(() => {
    const project = selectedProject.value
    if (!project) return []

    const map = new Map()
    projects.value
      .filter(p => p.source === project.source)
      .forEach(p => {
        if (p.space_id != null && !map.has(p.space_id)) {
          map.set(p.space_id, { id: p.space_id, name: p.space })
        }
      })

    return [...map.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ru')
    )
  })

  const openArchiveConfirm = () => {
    showArchiveConfirm.value = true
    actionsOpen.value = false
  }

  const submitArchive = async () => {
    const project = selectedProject.value
    if (!project) return
    archiveLoading.value = true
    try {
      await projectsApi.action(project.id, {
        action: 'move_to_bin',
        application: getApplication(project),
        space_id: project.space_id,
        filters: { ids: [String(project.id)] }
      })
      notifySuccess('Проект перемещён в архив')
      showArchiveConfirm.value = false
      closeDetail()
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    } finally {
      archiveLoading.value = false
    }
  }

  const submitRestore = async () => {
    const project = selectedProject.value
    if (!project) return
    actionsOpen.value = false
    try {
      const result = await projectsApi.action(project.id, {
        action: 'activate',
        application: getApplication(project),
        space_id: project.space_id,
        filters: { ids: [String(project.id)], status: ['bin'] }
      })

      if (result?.message) {
        toast.add({
          severity: 'warn',
          summary: 'Не удалось восстановить',
          detail: result.message,
          life: 5000
        })
        return
      }

      notifySuccess('Проект восстановлен')
      closeDetail()
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    }
  }

  const openDeleteConfirm = () => {
    showDeleteConfirm.value = true
    actionsOpen.value = false
  }

  const submitDelete = async () => {
    const project = selectedProject.value
    if (!project) return
    deleteLoading.value = true
    try {
      await projectsApi.action(project.id, {
        action: 'delete',
        application: getApplication(project),
        space_id: project.space_id,
        filters: { ids: [String(project.id)], status: ['bin'] }
      })
      notifySuccess('Проект удалён')
      showDeleteConfirm.value = false
      closeDetail()
      await fetchProjects()
    } catch (e) {
      notifyError(e)
    } finally {
      deleteLoading.value = false
    }
  }

  const buildActionsFor = project => {
    if (!project) return []
    if (project.is_deleted) return []

    if (project.is_archived) {
      return [
        {
          label: 'Открыть отчёт',
          icon: 'pi pi-chart-bar',
          handler: handleOpenReports
        },
        {
          label: 'Восстановить',
          icon: 'pi pi-refresh',
          handler: submitRestore,
          divider: true
        },
        {
          label: 'Удалить',
          icon: 'pi pi-trash',
          handler: openDeleteConfirm,
          danger: true,
          divider: true
        }
      ]
    }

    const isCollecting = project.status === 'collecting'

    return [
      {
        label: 'Открыть отчёт',
        icon: 'pi pi-chart-bar',
        handler: handleOpenReports
      },
      {
        label: 'Переименовать',
        icon: 'pi pi-pencil',
        handler: openRenameDialog
      },
      {
        label: isCollecting ? 'Отключить сборы' : 'Включить сборы',
        icon: isCollecting ? 'pi pi-pause' : 'pi pi-play',
        handler: toggleSync,
        disabled: !isCollecting && isLimitExceeded(project)
      },
      {
        label: 'Настройки',
        icon: 'pi pi-cog',
        handler: () => {
          const url = buildSettingsUrl(project)
          if (url) window.open(url, '_blank')
        }
      },
      {
        label: 'Копировать',
        icon: 'pi pi-copy',
        handler: openCopyDialog,
        divider: true
      },
      {
        label: 'В архив',
        icon: 'pi pi-inbox',
        handler: openArchiveConfirm,
        danger: true,
        divider: true
      }
    ]
  }

  const projectActions = computed(() => buildActionsFor(selectedProject.value))
  const rowProjectActions = computed(() =>
    buildActionsFor(rowActionsProject.value)
  )

  const handleActionClick = action => {
    action.handler()
    actionsOpen.value = false
  }

  const toggleRowActions = (project, event) => {
    event.stopPropagation()
    if (rowActionsOpenKey.value === project.key) {
      rowActionsOpenKey.value = null
      rowActionsProject.value = null
    } else {
      rowActionsOpenKey.value = project.key
      rowActionsProject.value = project
    }
  }

  const handleRowActionClick = (action, event) => {
    event.stopPropagation()
    selectedProject.value = rowActionsProject.value
    action.handler()
    rowActionsOpenKey.value = null
    rowActionsProject.value = null
  }

  const parseHistoryResponse = raw => {
    if (!raw) return []

    const flat = raw?.original?.data
    if (Array.isArray(flat)) {
      return flat
        .map(row => ({
          time: row.event_time ?? '',
          actor: row.actor ?? '',
          description: row.description ?? ''
        }))
        .filter(item => item.time || item.description)
    }

    const fieldOrder = Object.keys(raw?.meta?.dimensions ?? {})
    const timeIdx = fieldOrder.indexOf('event_time')
    const actIdx = fieldOrder.indexOf('actor')
    const descIdx = fieldOrder.indexOf('description')

    return (raw?.data ?? [])
      .map(row => {
        const dims = row?.dimensions ?? []
        return {
          time: dims[timeIdx]?.value ?? '',
          actor: dims[actIdx]?.value ?? '',
          description: dims[descIdx]?.value ?? ''
        }
      })
      .filter(item => item.time || item.description || item.actor)
  }

  const fetchHistory = async project => {
    if (!project?.space_id || !project?.id) return
    historyLoading.value = true
    historyError.value = ''
    try {
      const from = '1970-01-01'
      const to = new Date().toISOString().slice(0, 10)

      const response = await reportingApi.getSpaceReports(project.space_id, {
        report: 'platform.audit_log',
        periods: `${from}:${to}`,
        dimensions: 'event_time,description,action,actor',
        filters: `resource_id<in>${project.id}`,
        order_by: '+event_time',
        offset: 0,
        format: 'JSON'
      })

      console.log('history raw:', response)

      historyItems.value = parseHistoryResponse(response)
    } catch (e) {
      historyError.value = e?.message || 'Не удалось загрузить историю'
      historyItems.value = []
    } finally {
      historyLoading.value = false
    }
  }

  const columns = [
    { field: 'id', header: 'ID', label: 'По ID', nowrap: true },
    { field: 'name', header: 'Название', label: 'По названию', nowrap: true },
    {
      field: 'source',
      header: 'Источник',
      label: 'По источнику',
      nowrap: true
    },
    {
      field: 'space',
      header: 'Пространство',
      label: 'По пространству',
      nowrap: true
    },
    {
      field: 'regions_count',
      header: 'Регионы',
      label: 'По регионам',
      align: 'right',
      nowrap: true
    },
    {
      field: 'devices_count',
      header: 'Устройства',
      label: 'По устройствам',
      align: 'right',
      nowrap: true
    },
    {
      field: 'schedule',
      header: 'Расписание',
      label: 'По расписанию',
      nowrap: true
    },
    { field: 'status', header: 'Статус', label: 'По статусу', nowrap: true },
    {
      field: 'queries',
      header: 'Запросов',
      label: 'По запросам',
      align: 'right',
      nowrap: true
    },
    {
      field: 'last_sync',
      header: 'Последний синк',
      label: 'По последнему синку'
    },
    { field: '_actions', header: '', label: '', nowrap: true, sortable: false }
  ]

  const sortableColumns = computed(() =>
    columns.filter(c => c.sortable !== false)
  )

  const sourceLabels = { dfAi: 'ai', direct: 'context' }
  const sourceClasses = {
    dfAi: 'bg-indigo-100 text-indigo-800',
    direct: 'bg-orange-100 text-orange-800'
  }
  const statusLabels = {
    collecting: 'Сборы идут',
    stopped: 'Сборы не идут',
    archived: 'В архиве',
    deleted: 'Удален'
  }
  const statusClasses = {
    collecting: 'bg-green-100 text-green-800',
    stopped: 'bg-gray-100 text-gray-600',
    archived: 'bg-blue-50 text-blue-700',
    deleted: 'bg-red-50 text-red-600'
  }

  const DETAIL_SECTIONS = [
    {
      title: 'Основные настройки',
      rows: [
        { label: 'ID проекта', value: p => p.id },
        { label: 'Пространство', value: p => `${p.space} (${p.space_id})` },
        {
          label: 'Источник',
          value: p => sourceLabels[p.source] || p.source,
          badge: true,
          badgeClass: p => sourceClasses[p.source]
        },
        { label: 'Тематика', value: p => p.topic },
        { label: 'Стандарт обслуживания', value: p => p.service_standard },
        {
          label: 'Статус сборов',
          value: p => statusLabels[p.status] || p.status,
          badge: true,
          badgeClass: p => statusClasses[p.status]
        },
        {
          label: 'Лимиты',
          value: p => (isLimitExceeded(p) ? 'Лимит исчерпан' : ''),
          badge: true,
          badgeClass: () => 'bg-red-50 text-red-700'
        }
      ]
    },
    {
      title: 'Сбор SERP',
      rows: [
        {
          label: 'Запросов',
          value: p => p.queries.toLocaleString(),
          alwaysShow: true
        },
        { label: 'Расписание', value: p => p.schedule },
        { label: 'Регионы', value: p => p.regions_list.join(', ') },
        { label: 'Устройства', value: p => p.devices_list.join(', ') },
        { label: 'Поисковые системы', value: p => p.sources },
        { label: 'Глубина', value: p => p.depth },
        { label: 'Доменная зона', value: p => p.domain_zone }
      ],
      action: {
        label: 'Перейти в настройки проекта',
        icon: 'pi-cog',
        href: p => buildSettingsUrl(p)
      }
    },
    {
      title: 'Сбор Частотки',
      rows: [
        { label: 'Расписание', value: p => p.ws_schedule },
        { label: 'Устройства', value: p => p.ws_devices_list.join(', ') },
        { label: 'Регионы', value: p => p.ws_regions_list.join(', ') },
        { label: 'Типы частотки', value: p => p.ws_types }
      ]
    },
    {
      title: 'Служебная информация',
      rows: [
        { label: 'Вендор SERP', value: p => p.serp_vendor },
        { label: 'Вендор частотки', value: p => p.ws_vendor },
        {
          label: 'Последний синк',
          value: p =>
            p.last_sync
              ? `${formatDate(p.last_sync)} ${formatTime(p.last_sync)}`
              : ''
        }
      ]
    }
  ]

  const detailSections = computed(() => {
    const project = selectedProject.value
    if (!project) return []
    return DETAIL_SECTIONS.map(section => ({
      title: section.title,
      action:
        section.action && !project.is_deleted
          ? {
              label: section.action.label,
              icon: section.action.icon,
              href: section.action.href(project)
            }
          : null,
      rows: section.rows
        .map(row => ({
          label: row.label,
          value: row.value(project),
          badge: row.badge,
          badgeClass: row.badgeClass
            ? row.badgeClass(project) || 'bg-gray-100 text-gray-600'
            : '',
          alwaysShow: row.alwaysShow
        }))
        .filter(
          row => row.alwaysShow || (row.value != null && row.value !== '')
        )
    })).filter(section => section.rows.length > 0)
  })

  const spaces = computed(() => {
    const map = new Map()
    projects.value.forEach(p => {
      if (p.space_id != null && !map.has(p.space_id))
        map.set(p.space_id, { id: p.space_id, name: p.space })
    })
    return [...map.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'ru')
    )
  })

  const filteredSpaces = computed(() => {
    const query = spaceSearch.value.toLowerCase()
    if (!query) return spaces.value
    return spaces.value.filter(
      s =>
        String(s.name).toLowerCase().includes(query) ||
        String(s.id).includes(query)
    )
  })

  const selectedSpaceLabel = computed(() => {
    if (!spaceFilter.value) return 'все'
    return spaces.value.find(s => s.id === spaceFilter.value)?.name ?? 'все'
  })

  const buildStatusParams = () => {
    const statuses = ['active']
    if (showArchived.value) statuses.push('bin')
    if (showDeleted.value) statuses.push('deleted')
    const params = new URLSearchParams()
    statuses.forEach(status => params.append('filters[status][]', status))
    return params
  }

  const filteredProjects = computed(() => {
    let list = projects.value
    if (!showArchived.value) list = list.filter(p => !p.is_archived)
    if (!showDeleted.value) list = list.filter(p => !p.is_deleted)
    if (spaceFilter.value)
      list = list.filter(p => p.space_id === spaceFilter.value)
    if (sourceFilter.value)
      list = list.filter(p => p.source === sourceFilter.value)
    const query = search.value.trim().toLowerCase()
    if (query)
      list = list.filter(
        p =>
          String(p.id).includes(query) || p.name.toLowerCase().includes(query)
      )
    const dir = sortAsc.value ? 1 : -1
    const field = sortField.value
    return [...list].sort((a, b) => {
      const av = a[field],
        bv = b[field]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number')
        return (av - bv) * dir
      return String(av).localeCompare(String(bv), 'ru') * dir
    })
  })

  const DAY_NAMES = {
    1: 'Пн',
    2: 'Вт',
    3: 'Ср',
    4: 'Чт',
    5: 'Пт',
    6: 'Сб',
    7: 'Вс'
  }
  const SOURCE_ENGINE_LABELS = { yandex: 'Яндекс', google: 'Google' }
  const DEVICE_LABELS = {
    desktop: 'Desktop',
    'mobile-ios': 'Mobile (iOS)',
    'mobile-android': 'Mobile (Android)',
    mobile: 'Mobile'
  }

  const toSettingsList = value => {
    if (Array.isArray(value)) return value
    if (!value || typeof value !== 'object') return []
    if ('device' in value || 'region_id' in value) return [value]
    return Object.values(value).filter(v => v && typeof v === 'object')
  }

  const listRegions = settings => {
    const names = new Set()
    for (const s of toSettingsList(settings)) {
      const name = s?.region_name ?? s?.region?.name ?? s?.region_id
      if (name != null && name !== '') names.add(String(name))
    }
    return [...names]
  }

  const listDevices = settings => {
    const keys = new Set()
    for (const s of toSettingsList(settings)) {
      if (!s?.device) continue
      const os = s.os ?? s.operating_system ?? ''
      keys.add(s.device === 'mobile' && os ? `mobile-${os}` : s.device)
    }
    return [...keys].map(key => DEVICE_LABELS[key] || key)
  }

  const formatWsTypes = settings => {
    let hasBase = false,
      hasCollection = false
    for (const s of toSettingsList(settings)) {
      for (const t of s?.types || []) {
        if (String(t).includes('"')) hasCollection = true
        else hasBase = true
      }
    }
    const parts = []
    if (hasBase) parts.push('Base')
    if (hasCollection) parts.push('Collection')
    return parts.join('+')
  }

  const uniqueValues = (settings, field) => {
    const values = new Set()
    for (const s of toSettingsList(settings)) {
      const v = s?.[field]
      if (v != null && v !== '') values.add(String(v))
    }
    return [...values]
  }

  const listVendors = settings => uniqueValues(settings, 'vendor')

  const formatDaysPart = timetable => {
    if (!timetable.days?.length) return ''
    return [...timetable.days]
      .sort((a, b) => a - b)
      .map(d => DAY_NAMES[d])
      .filter(Boolean)
      .join(', ')
  }

  const formatHoursPart = timetable => {
    const rawHours = timetable.hours
    if (rawHours == null || rawHours === '') return ''
    const hours = (
      Array.isArray(rawHours) ? rawHours : String(rawHours).split(',')
    )
      .map(h => String(h).trim())
      .filter(Boolean)
      .map(h => `${h.padStart(2, '0')}:00`)
    return hours.length ? `Ежедневно, ${hours.join(', ')}` : ''
  }

  const formatSchedule = (timetable, scheduleType) => {
    if (!timetable) return ''
    if (scheduleType === 'hour' || scheduleType === 'weekly_interval_hour')
      return formatHoursPart(timetable) || formatDaysPart(timetable)
    if (scheduleType === 'weekly_interval')
      return formatDaysPart(timetable) || formatHoursPart(timetable)
    return formatDaysPart(timetable) || formatHoursPart(timetable)
  }

  const parseSyncDate = folder => {
    const raw = folder?.lastUpdatedAt?.date
    if (!raw) return null
    return raw.split('.')[0].replace(' ', 'T')
  }

  const resolveStatus = p => {
    if (p.project_status === 'deleted') return 'deleted'
    if (p.project_status === 'bin') return 'archived'
    if (
      p.root_semantics_folder?.active === false ||
      p.serp_schedule_timetable === null
    )
      return 'stopped'
    if (p.project_status === 'active' && p.ws_schedule_timetable != null)
      return 'collecting'
    return 'stopped'
  }

  const mapProject = (p, source, index) => {
    const id = p.id ?? p.project_id ?? null
    const serpRegions = listRegions(p.serp_settings)
    const serpDevices = listDevices(p.serp_settings)
    return {
      key: `${source}-${id ?? `idx${index}`}`,
      source,
      id: p.project_id,
      name: p.name,
      space: p.space_name ?? '',
      space_id: p.space_id ?? null,
      regions_count: serpRegions.length,
      regions_list: serpRegions,
      devices_count: serpDevices.length,
      devices_list: serpDevices,
      schedule: formatSchedule(p.serp_schedule_timetable, p.serp_schedule_type),
      status: resolveStatus(p),
      queries: p.root_semantics_folder?.keywordsCountOnLastSync ?? 0,
      last_sync: parseSyncDate(p.root_semantics_folder),
      is_archived: p.project_status === 'bin',
      is_deleted: p.project_status === 'deleted',
      topic: p.topic ?? p.theme ?? '',
      service_standard: p.service_standard ?? '',
      sources: uniqueValues(p.serp_settings, 'source')
        .map(s => SOURCE_ENGINE_LABELS[s] || s)
        .join(', '),
      depth: uniqueValues(p.serp_settings, 'top').join(', '),
      domain_zone: uniqueValues(p.serp_settings, 'domain_zone')
        .map(z => `.${z}`)
        .join(', '),
      ws_schedule: formatSchedule(p.ws_schedule_timetable, p.ws_schedule_type),
      ws_regions_list: listRegions(p.ws_settings),
      ws_devices_list: listDevices(p.ws_settings),
      ws_types: formatWsTypes(p.ws_settings),
      serp_vendor: listVendors(p.serp_settings).join(', '),
      ws_vendor: listVendors(p.ws_settings).join(', ')
    }
  }

  const extractGroup = (response, key) => {
    const data = response?.data ?? response
    const group = data?.[key]
    if (Array.isArray(group)) return { items: group, error: null }
    const message =
      group?.message ??
      group?.data?.message ??
      (group ? 'Неизвестная ошибка' : null)
    return { items: [], error: message }
  }

  const fetchProjects = async () => {
    loading.value = true
    error.value = ''
    noData.value = false
    try {
      const params = buildStatusParams()
      const response = await projectsApi.getList(params)
      const ai = extractGroup(response, 'ai_projects')
      const context = extractGroup(response, 'context_projects')
      projects.value = [
        ...ai.items.map((p, i) => mapProject(p, 'dfAi', i)),
        ...context.items.map((p, i) => mapProject(p, 'direct', i))
      ]

      if (selectedProject.value) {
        selectedProject.value =
          projects.value.find(p => p.key === selectedProject.value.key) ?? null
      }
      const failed = []
      if (ai.error) failed.push(`ai: ${ai.error}`)
      if (context.error) failed.push(`context: ${context.error}`)
      if (failed.length && projects.value.length === 0) {
        error.value = failed.join('; ')
      } else if (failed.length) {
        toast.add({
          severity: 'warn',
          summary: 'Частичная загрузка',
          detail: failed.join('; '),
          life: 5000
        })
      } else {
        noData.value = projects.value.length === 0
      }
    } catch {
      projects.value = []
      error.value = 'Ошибка загрузки данных'
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось загрузить список проектов',
        life: 3000
      })
    } finally {
      loading.value = false
    }
  }

  const extractLimits = response => {
    const data = response?.data ?? response
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.data)) return data.data
    if (Array.isArray(data?.limits)) return data.limits
    if (Array.isArray(data?.spaces)) return data.spaces
    return []
  }

  const fetchLimits = async () => {
    try {
      const response = await limitsApi.getSpacesLimits()
      const items = extractLimits(response)
      const stopped = new Set()
      items.forEach(item => {
        if (item?.data_collection_status?.status === 'stopped')
          stopped.add(limitKey(item.application, item.space_id))
      })
      limitStoppedSpaces.value = stopped
    } catch (err) {
      console.error('Ошибка загрузки лимитов:', err)
      limitStoppedSpaces.value = new Set()
    }
  }

  // ─── UI helpers ───────────────────────────────────────────────────────────
  const toggleSort = field => {
    if (sortField.value === field) sortAsc.value = !sortAsc.value
    else {
      sortField.value = field
      sortAsc.value = true
    }
  }

  const toggleSpaceDropdown = async () => {
    spaceOpen.value = !spaceOpen.value
    if (spaceOpen.value) {
      spaceSearch.value = ''
      await nextTick()
      spaceSearchRef.value?.focus()
    }
  }

  const selectSpace = id => {
    spaceFilter.value = id
    spaceOpen.value = false
  }

  const handleClickOutside = event => {
    if (
      spaceOpen.value &&
      spaceDropdownRef.value &&
      !spaceDropdownRef.value.contains(event.target)
    )
      spaceOpen.value = false

    const dropdown = event.target.closest('[data-actions-dropdown]')
    const id = dropdown?.dataset.actionsDropdown

    if (actionsOpen.value && id !== 'panel') actionsOpen.value = false

    if (rowActionsOpenKey.value && id !== String(rowActionsOpenKey.value)) {
      rowActionsOpenKey.value = null
      rowActionsProject.value = null
    }
  }

  const resetFilters = () => {
    search.value = ''
    spaceFilter.value = ''
    sourceFilter.value = ''
    showArchived.value = false
    showDeleted.value = false
    sortField.value = 'id'
    sortAsc.value = true
    spaceSearch.value = ''
    spaceOpen.value = false
  }

  const openDetail = project => {
    selectedProject.value = project
    actionsOpen.value = false
    activeTab.value = 'overview'
    historyItems.value = []
    historyError.value = ''
  }

  const closeDetail = () => {
    selectedProject.value = null
    actionsOpen.value = false
    activeTab.value = 'overview'
    historyItems.value = []
    historyError.value = ''
  }

  const handleEscape = event => {
    if (event.key !== 'Escape') return
    if (showDeleteConfirm.value) showDeleteConfirm.value = false
    else if (showArchiveConfirm.value) showArchiveConfirm.value = false
    else if (showCopyDialog.value) showCopyDialog.value = false
    else if (showRenameDialog.value) showRenameDialog.value = false
    else if (rowActionsOpenKey.value) {
      rowActionsOpenKey.value = null
      rowActionsProject.value = null
    } else if (actionsOpen.value) actionsOpen.value = false
    else if (selectedProject.value) closeDetail()
  }

  const formatDate = date => (date ? new Date(date).toLocaleDateString() : '')
  const formatTime = date =>
    date
      ? new Date(date).toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit'
        })
      : ''

  const formatHistoryDate = value => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date
      .toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      .replace(',', '')
  }

  watch(showArchived, () => fetchProjects())
  watch(showDeleted, () => fetchProjects())

  watch(activeTab, tab => {
    if (tab === 'history' && historyItems.value.length === 0) {
      fetchHistory(selectedProject.value)
    }
  })

  onMounted(() => {
    fetchProjects()
    fetchLimits()
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
  })
</script>
